"use client";

import { gsap } from "gsap";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Star, Users, MessageSquare } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const FeedbackHeroSection = () => {
  const root = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
      });

      tl.from(".hero-eyebrow", {
        y: 30,
        opacity: 0,
        duration: 0.8,
      })
        .from(
          ".hero-line",
          {
            y: 80,
            opacity: 0,
            duration: 1.1,
            stagger: 0.12,
          },
          "-=0.5",
        )
        .from(
          ".hero-sub",
          {
            y: 40,
            opacity: 0,
            duration: 1,
          },
          "-=0.6",
        )
        .from(
          ".hero-pill",
          {
            scale: 0.85,
            opacity: 0,
            duration: 0.6,
            stagger: 0.1,
          },
          "-=0.7",
        );

      gsap.to(imageRef.current, {
        yPercent: 18,
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  const pills = [
    {
      icon: <Star className="w-3.5 h-3.5" />,
      label: "Verified Reviews",
    },
    {
      icon: <Users className="w-3.5 h-3.5" />,
      label: "Real Clients",
    },
    {
      icon: <MessageSquare className="w-3.5 h-3.5" />,
      label: "Success Stories",
    },
  ];
  const gradient = "linear-gradient(110.23deg, #7949CD 6.47%, #6B58F5 44.08%, #0EA7F0 83.15%)";
  const stats = [
    {
      icon: "/images/icons/users.webp",
      title: "Happy\nClients",
      value: "100+",
    },
    {
      icon: "/images/icons/Frame.webp",
      title: "Projects\nDelivered",
      value: "300+",
    },
    {
      icon: "/images/icons/globeIcon.webp",
      title: "Countries\nServed",
      value: "10+",
    },
    {
      icon: "/images/icons/trophyIcon.webp",
      title: "Retained & Referred Clients",
      value: "50+",
    },
  ];
  return (
    <section ref={root} className="relative hero-section w-full overflow-hidden bg-background-primary !px-4">
      {/* Mobile background (light) */}
      <div className="relative block lg:hidden w-full h-[260px] sm:h-[340px] dark:hidden">
        <Image src="/images/serviceBanners/TestimonialsW.webp" alt="feedback" fill priority className="object-cover object-right" />
      </div>
      {/* Mobile background (dark) */}
      <div className="relative hidden w-full h-[260px] sm:h-[340px] dark:block lg:dark:hidden">
        <Image src="/images/serviceBanners/Testimonials.webp" alt="feedback" fill priority className="object-cover object-right" />
      </div>

      {/* Desktop background (wrapper with ref={imageRef} for GSAP) */}
      <div ref={imageRef} className="absolute inset-0 w-full h-full top-0 hidden lg:block">
        <Image
          src="/images/serviceBanners/TestimonialsW.webp"
          alt="Feedback Hero"
          fill
          priority
          sizes="(max-width: 1024px) 100vw, 1200px"
          className="object-contain object-right dark:hidden"
          quality={75}
        />
        <Image
          src="/images/serviceBanners/Testimonials.webp"
          alt="Feedback Hero"
          fill
          priority
          sizes="(max-width: 1024px) 100vw, 1200px"
          className="object-contain object-right hidden dark:block"
          quality={75}
        />
      </div>

      {/* Light Overlay */}
      <div
        className="absolute inset-0 z-10 hidden lg:block dark:hidden"
        style={{
          background:
            "linear-gradient(90deg, rgba(240, 242, 255, 0.95) 0%, rgba(240, 242, 255, 1) 45%, rgba(240, 242, 255, 0.9) 50%, rgba(240, 242, 255, 0.25) 65%,  transparent 100%)",
        }}
      />
      {/* Dark Overlay */}
      <div
        className="absolute inset-0 z-10 hidden dark:lg:block"
        style={{
          background: "linear-gradient(90deg, #0d0f1e 0%, #0d0f1e 28%, rgba(13, 15, 30, 1) 15%, rgba(13, 15, 30, 0.3) 65%, transparent 100%)",
        }}
      />

      <div className="relative z-20 flex flex-col justify-center  px-6! py-4! md:py-10!">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4 mb-4! hero-eyebrow">
            <h3 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">CLIENT FEEDBACK</h3>

            <span className="w-16 h-[2px] bg-[#8B3DFF] rounded-full"></span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-6!">
            <span className="hero-line text-text-primary">Voices of</span>

            <span className="hero-line text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600"> Success.</span>
          </h1>

          <p className="hero-sub text-text-secondary text-sm sm:text-base lg:text-[16px] 2xl:text-lg md:max-w-[90vw] lg:max-w-[40vw] leading-relaxed !mb-12 w-full">
            Discover real success stories from businesses that transformed their digital presence through our custom web development, mobile application solutions, and advanced
            technology expertise. Our scalable and high performance digital solutions help brands enhance user engagement, improve operational efficiency, and accelerate
            sustainable business growth. From modern web platforms to feature rich mobile applications, we focus on delivering impactful experiences that strengthen brand identity
            and customer trust. Each project reflects our commitment to innovation, quality, and long term success, helping businesses achieve measurable results and stand out in a
            competitive digital landscape with reliable and future ready technology solutions.
          </p>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto mb-8! lg:mb-16!">
          <div className="grid grid-cols-2 lg:grid-cols-8 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center group">
                <div className="relative flex items-center justify-center">
                  <div className="relative flex items-center justify-center border border-transparent bg-transparent backdrop-blur-sm group-hover:scale-105 transition-transform duration-300">
                    <img src={item.icon} alt="icons" className="h-14" />
                  </div>
                </div>

                <p
                  className="mt-5! text-2xl font-bold tracking-tighter"
                  style={{
                    background: gradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {item.value}
                </p>
                <h3 className="mt-1! font-medium text-text-secondary lg:text-md text-sm font-medium leading-tight">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-section {
          min-height: 80vh;
        }

        @media screen and (min-width: 1024px) and (max-width: 1440px) and (max-height: 850px) {
          .hero-section {
            min-height: 550px;
          }
        }

        @media screen and (min-width: 1024px) and (max-height: 768px) {
          .hero-section {
            min-height: 500px;
          }
        }

        @media screen and (min-width: 1441px) {
          .hero-section {
            min-height: 650px;
          }
        }
      `}</style>
    </section>
  );
};

export default FeedbackHeroSection;
